import {
  DECISION_SOURCE_DUO_FLOW,
  DECISION_SOURCE_MANUAL,
  DECISION_SOURCE_THREAD_RESOLUTION,
} from './constants';

/**
 * Works out where a decision came from.
 *
 * The API has no source field, so the card reads the mark each path leaves on the record. A source
 * link is only ever set by an author filling in the form, and a discussion id is only ever set by
 * marking a thread. Duo is the remaining case, because nothing else writes a decision.
 *
 * @param {Object} decision A decision from the decision log query.
 * @returns {string} One of the DECISION_SOURCE_* values.
 */
export const decisionSource = ({ sourceLink, discussionId }) => {
  if (sourceLink) return DECISION_SOURCE_MANUAL;
  if (discussionId) return DECISION_SOURCE_THREAD_RESOLUTION;

  return DECISION_SOURCE_DUO_FLOW;
};

/**
 * The options a decision settled on. Only a selected option counts as settled.
 *
 * @param {Object} decision A decision from the decision log query.
 * @returns {Array<Object>} The settled options, in the order they were fetched.
 */
export const settledDecisionOptions = (decision) =>
  (decision?.options?.nodes || []).filter((option) => option.selected);

/**
 * The decision itself, as the card heads it.
 *
 * The text lives on the settled option. A record with no settled option should not exist, because
 * a decision is only logged once an option is chosen, so the title stands in for it.
 *
 * @param {Object} decision A decision from the decision log query.
 * @returns {string} The decision text, or an empty string when there is none.
 */
export const decisionText = (decision) =>
  settledDecisionOptions(decision)[0]?.content || decision?.title || '';

/**
 * The one link a decision points at.
 *
 * A decision recorded by hand carries the link its author gave. One resolved from a thread carries
 * the comment that settled it, which the author cannot change.
 *
 * @param {Object} decision A decision from the decision log query.
 * @returns {string} The link, or an empty string when there is none.
 */
export const decisionSourceUrl = (decision) => decision?.sourceLink || decision?.noteUrl || '';
